import React, { useState, useEffect } from "react";
import classes from "./VisitDetailModal.module.css";
import { 
    FaTimes, FaCalendarAlt, FaClock, FaUserMd, 
    FaStethoscope, FaFlask, FaPills, FaFileAlt, 
    FaPlus, FaUpload, FaCheckCircle, FaSearch
} from "react-icons/fa";

const VisitDetailModal = ({ isOpen, onClose, visitData }) => {
    // 1. Local State to make the modal fully editable
    const [formData, setFormData] = useState({
        complaint: "",
        history: "Patient reports intermittent chest pain, mild shortness of breath on exertion. No fever.",
        physical: "BP: 128/80 mmHg, Pulse: 78/min regular. RS: Clear, CVS: S1 S2 normal, No murmur. No pedal edema.",
        vitals: { temp: "98.6", pulse: "78", bpSys: "128", bpDia: "80", rr: "18", spo2: "98", weight: "72", height: "175" },
        diagnosis: ["R079 - Chest pain, unspecified"],
        plan: "Advised ECG and TMT. Start Tab Aspirin 75mg OD after food. Follow up after test reports.",
        medications: ["Tab Aspirin 75mg", "Tab Atorvastatin 20mg"],
        tests: ["ECG", "TMT (Treadmill Test)"],
        followUpDate: "2024-05-28",
        followUpTime: "09:00",
        attachments: [{ name: "Previous ECG Report.pdf", size: "245 KB" }]
    });

    // 2. State to handle the "Add New" input boxes
    const [newMedication, setNewMedication] = useState("");
    const [newTest, setNewTest] = useState("");
    const [newDiagnosis, setNewDiagnosis] = useState("");

    // 3. When a new Visit is clicked, reset the state with that visit's details
    useEffect(() => {
        if (visitData) {
            setFormData(prev => ({
                ...prev,
                complaint: visitData.reason || "",
            }));
        }
    }, [visitData]);

    if (!isOpen || !visitData) return null;

    // --- HANDLERS ---
    const handleTextChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleVitalChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            vitals: { ...prev.vitals, [name]: value }
        }));
    };

    // Add items typed into the input boxes
    const addMedication = () => {
        if (newMedication.trim() !== "") {
            setFormData(prev => ({
                ...prev,
                medications: [...prev.medications, newMedication.trim()]
            }));
            setNewMedication(""); // Clear the input
        }
    };

    const addTest = () => {
        if (newTest.trim() !== "") {
            setFormData(prev => ({
                ...prev,
                tests: [...prev.tests, newTest.trim()]
            }));
            setNewTest(""); // Clear the input
        }
    };

    const addDiagnosis = () => {
        if (newDiagnosis.trim() !== "") {
            setFormData(prev => ({
                ...prev,
                diagnosis: [...prev.diagnosis, newDiagnosis.trim()]
            }));
            setNewDiagnosis(""); // Clear the input
        }
    };

    const removeItem = (field, index) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    const handleSaveVisit = () => {
        console.log("Saving visit data:", formData);
        alert("Visit saved successfully! Check console for data.");
        onClose();
    };

    return (
        <div className={classes.modalOverlay}>
            <div className={classes.modalContainer}>
                
                {/* --- HEADER --- */}
                <div className={classes.modalHeader}>
                    <div className={classes.headerLeft}>
                        <div className={classes.headerIcon}><FaFileAlt /></div>
                        <div>
                            <h2>Record Patient Visit</h2>
                            <p>Capture clinical details and observations for this visit</p>
                        </div>
                    </div>
                    <button className={classes.closeBtn} onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className={classes.modalBody}>
                    
                    {/* --- TOP ROW: VISIT DETAILS --- */}
                    <div className={classes.sectionContainer}>
                        <div className={classes.grid3Col}>
                            <div className={classes.fieldGroup}>
                                <label>Visit Type <span className={classes.required}>*</span></label>
                                <div className={classes.readOnlyField}>{visitData.type}</div>
                            </div>
                            <div className={classes.fieldGroup}>
                                <label>Visit Date <span className={classes.required}>*</span></label>
                                <div className={classes.readOnlyField}><FaCalendarAlt /> {visitData.date}</div>
                            </div>
                            <div className={classes.fieldGroup}>
                                <label>Visit Time <span className={classes.required}>*</span></label>
                                <div className={classes.readOnlyField}><FaClock /> {visitData.time}</div>
                            </div>
                        </div>

                        <div className={classes.grid3Col}>
                            <div className={classes.fieldGroup}>
                                <label>Department <span className={classes.required}>*</span></label>
                                <div className={classes.readOnlyField}>{visitData.dept}</div>
                            </div>
                            <div className={classes.fieldGroup}>
                                <label>Doctor <span className={classes.required}>*</span></label>
                                <div className={classes.readOnlyField}><FaUserMd /> {visitData.doctor}</div>
                            </div>
                            <div className={classes.fieldGroup}>
                                <label>Location / Room</label>
                                <div className={classes.readOnlyField}>OPD - Room 2</div>
                            </div>
                        </div>

                        <div className={classes.fullWidthGroup}>
                            <label>Chief Complaint (Reason for Visit) <span className={classes.required}>*</span></label>
                            <textarea 
                                className={classes.textAreaActive} 
                                name="complaint"
                                value={formData.complaint} 
                                onChange={handleTextChange}
                                rows={2}
                            />
                        </div>
                    </div>

                    {/* --- HISTORY & PHYSICAL --- */}
                    <div className={classes.sectionContainer}>
                        <div className={classes.grid2Col}>
                            <div className={classes.fieldGroup}>
                                <label>History of Present Illness</label>
                                <textarea 
                                    className={classes.textAreaActive} 
                                    name="history"
                                    value={formData.history} 
                                    onChange={handleTextChange}
                                    rows={3}
                                />
                            </div>
                            <div className={classes.fieldGroup}>
                                <label>Physical Examination</label>
                                <textarea 
                                    className={classes.textAreaActive} 
                                    name="physical"
                                    value={formData.physical} 
                                    onChange={handleTextChange}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* --- VITALS --- */}
                    <div className={classes.sectionContainer}>
                        <h3 className={classes.subSectionTitle}>Vitals</h3>
                        <div className={classes.grid4Col}>
                            <div className={classes.vitalField}>
                                <label>Temperature</label>
                                <div className={classes.vitalInputGroup}>
                                    <input type="text" name="temp" value={formData.vitals.temp} onChange={handleVitalChange} />
                                    <span>°F</span>
                                </div>
                            </div>
                            <div className={classes.vitalField}>
                                <label>Pulse</label>
                                <div className={classes.vitalInputGroup}>
                                    <input type="text" name="pulse" value={formData.vitals.pulse} onChange={handleVitalChange} />
                                    <span>/min</span>
                                </div>
                            </div>
                            <div className={classes.vitalField}>
                                <label>Blood Pressure</label>
                                <div className={classes.vitalInputGroupBP}>
                                    <input type="text" name="bpSys" value={formData.vitals.bpSys} onChange={handleVitalChange} />
                                    <span>/</span>
                                    <input type="text" name="bpDia" value={formData.vitals.bpDia} onChange={handleVitalChange} />
                                    <span>mmHg</span>
                                </div>
                            </div>
                            <div className={classes.vitalField}>
                                <label>Respiratory Rate</label>
                                <div className={classes.vitalInputGroup}>
                                    <input type="text" name="rr" value={formData.vitals.rr} onChange={handleVitalChange} />
                                    <span>/min</span>
                                </div>
                            </div>
                        </div>
                        <div className={classes.grid4Col} style={{ marginTop: '16px' }}>
                            <div className={classes.vitalField}>
                                <label>SpO2</label>
                                <div className={classes.vitalInputGroup}>
                                    <input type="text" name="spo2" value={formData.vitals.spo2} onChange={handleVitalChange} />
                                    <span>%</span>
                                </div>
                            </div>
                            <div className={classes.vitalField}>
                                <label>Weight</label>
                                <div className={classes.vitalInputGroup}>
                                    <input type="text" name="weight" value={formData.vitals.weight} onChange={handleVitalChange} />
                                    <span>kg</span>
                                </div>
                            </div>
                            <div className={classes.vitalField}>
                                <label>Height</label>
                                <div className={classes.vitalInputGroup}>
                                    <input type="text" name="height" value={formData.vitals.height} onChange={handleVitalChange} />
                                    <span>cm</span>
                                </div>
                            </div>
                            <div className={classes.vitalField}>
                                <label>BMI (Auto)</label>
                                <div className={classes.vitalReadOnly}><span>23.5</span> kg/m²</div>
                            </div>
                        </div>
                    </div>

                    {/* --- DIAGNOSIS & PLAN --- */}
                    <div className={classes.sectionContainer}>
                        <div className={classes.grid2Col}>
                            <div className={classes.fieldGroup}>
                                <label>Diagnosis <span className={classes.required}>*</span></label>
                                <div className={classes.tagList}>
                                    {formData.diagnosis.map((diag, index) => (
                                        <span key={index} className={classes.tag}>
                                            {diag} 
                                            <span className={classes.removeTag} onClick={() => removeItem('diagnosis', index)}>×</span>
                                        </span>
                                    ))}
                                    
                                    {/* Input to Add Diagnosis */}
                                    <div className={classes.inlineInputGroup}>
                                        <input 
                                            type="text" 
                                            placeholder="Type diagnosis & press Enter"
                                            value={newDiagnosis}
                                            onChange={(e) => setNewDiagnosis(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addDiagnosis()}
                                            className={classes.inlineInput}
                                        />
                                        <button className={classes.addBtnInline} onClick={addDiagnosis}><FaPlus /> Add</button>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.fieldGroup}>
                                <label>Plan / Treatment</label>
                                <textarea 
                                    className={classes.textAreaActive} 
                                    name="plan"
                                    value={formData.plan} 
                                    onChange={handleTextChange}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* --- MEDICATIONS & TESTS --- */}
                    <div className={classes.sectionContainer}>
                        <div className={classes.grid2Col}>
                            <div className={classes.fieldGroup}>
                                <label>Medications Prescribed</label>
                                <div className={classes.listBox}>
                                    {formData.medications.map((med, index) => (
                                        <div key={index} className={classes.listItem}>
                                            {index + 1}. <strong>{med}</strong> 
                                            <span className={classes.removeItem} onClick={() => removeItem('medications', index)}>×</span>
                                        </div>
                                    ))}
                                    
                                    {/* Input to Add Medication */}
                                    <div className={classes.inlineInputGroup}>
                                        <input 
                                            type="text" 
                                            placeholder="Search and add medication..."
                                            value={newMedication}
                                            onChange={(e) => setNewMedication(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addMedication()}
                                            className={classes.inlineInput}
                                        />
                                        <button className={classes.addBtnInline} onClick={addMedication}><FaPlus /> Add</button>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.fieldGroup}>
                                <label>Tests / Investigations Ordered</label>
                                <div className={classes.listBox}>
                                    {formData.tests.map((test, index) => (
                                        <div key={index} className={classes.listItem}>
                                            {index + 1}. <strong>{test}</strong> 
                                            <span className={classes.removeItem} onClick={() => removeItem('tests', index)}>×</span>
                                        </div>
                                    ))}

                                    {/* Input to Add Test */}
                                    <div className={classes.inlineInputGroup}>
                                        <input 
                                            type="text" 
                                            placeholder="Search and add test..."
                                            value={newTest}
                                            onChange={(e) => setNewTest(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addTest()}
                                            className={classes.inlineInput}
                                        />
                                        <button className={classes.addBtnInline} onClick={addTest}><FaPlus /> Add</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- FOLLOW UP & ATTACHMENTS --- */}
                    <div className={classes.sectionContainer}>
                        <div className={classes.grid2Col}>
                            <div className={classes.fieldGroup}>
                                <label>Follow Up</label>
                                <div className={classes.followUpRow}>
                                    <div className={classes.inputWithIcon}>
                                        <FaCalendarAlt className={classes.fieldIcon} />
                                        <input 
                                            type="date" 
                                            className={classes.dateInput} 
                                            value={formData.followUpDate}
                                            onChange={(e) => setFormData({...formData, followUpDate: e.target.value})}
                                        />
                                    </div>
                                    <div className={classes.inputWithIcon}>
                                        <FaClock className={classes.fieldIcon} />
                                        <input 
                                            type="time" 
                                            className={classes.dateInput} 
                                            value={formData.followUpTime}
                                            onChange={(e) => setFormData({...formData, followUpTime: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div className={classes.checkboxRow}>
                                    <input type="checkbox" id="followUp" defaultChecked />
                                    <label htmlFor="followUp">Patient advised to follow up</label>
                                </div>
                            </div>
                            
                            <div className={classes.fieldGroup}>
                                <label>Attachments (Optional)</label>
                                <div className={classes.uploadBox}>
                                    <FaUpload /> <span>Upload Files</span> <span className={classes.uploadSub}>(JPG, PNG, PDF, Max 10MB)</span>
                                </div>
                                {formData.attachments.map((file, index) => (
                                    <div key={index} className={classes.attachedFile}>
                                        <FaFileAlt /> {file.name} 
                                        <span className={classes.fileSize}>{file.size}</span> 
                                        <span className={classes.removeItem} onClick={() => removeItem('attachments', index)}>×</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>

                {/* --- FOOTER --- */}
                <div className={classes.modalFooter}>
                    <button className={classes.cancelBtn} onClick={onClose}>Cancel</button>
                    <div className={classes.rightFooter}>
                        <button className={classes.draftBtn} onClick={() => alert("Draft Saved!")}>Save as Draft</button>
                        <button className={classes.saveBtn} onClick={handleSaveVisit}><FaCheckCircle /> Save Visit</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VisitDetailModal;